export type Language = "en" | "ru" | "ar" | "de" | "ko" | "tr" | "ja" | "zh" | "pt" | "es" | "fr" | "it" | "pl";

export const languageNames: Record<Language, string> = {
  en: "English",
  ru: "Русский",
  ar: "العربية",
  de: "Deutsch",
  ko: "한국어",
  tr: "Türkçe",
  ja: "日本語",
  zh: "中文",
  pt: "Português",
  es: "Español",
  fr: "Français",
  it: "Italiano",
  pl: "Polski",
};

export const languageFlags: Record<Language, string> = {
  en: "🇬🇧",
  ru: "🇷🇺",
  ar: "🇸🇦",
  de: "🇩🇪",
  ko: "🇰🇷",
  tr: "🇹🇷",
  ja: "🇯🇵",
  zh: "🇨🇳",
  pt: "🇵🇹",
  es: "🇪🇸",
  fr: "🇫🇷",
  it: "🇮🇹",
  pl: "🇵🇱",
};

export const translations: Record<Language, Record<string, string>> = {
  en: {
    "nav.features": "Features",
    "nav.rewards": "Rewards",
    "nav.premium": "Premium",
    "nav.faq": "FAQ",
    "nav.getStarted": "Get Started",
    "hero.badge": "Now Available Worldwide",
    "hero.title1": "Spend Crypto\nLike Cash.",
    "hero.title2": "Everywhere.",
    "hero.desc":
      "The first card that connects directly to your crypto wallet. Spend from your wallet without account top-ups or verification.",
    "hero.issueCard": "Issue Card",
    "hero.learnMore": "Learn More",
    "hero.perk1": "No KYC",
    "hero.perk2": "Instant Approval",
    "hero.perk3": "Zero Annual Fee",
    "wallets.title": "Works with your favorite wallets",
    "backers.title": "Our Backers",
    "features.label": "Features",
    "features.title": "Everything you need. Nothing you don't.",
    "features.desc": "Built for the modern crypto user. Every feature designed to make your life easier.",
    "features.1.title": "Direct Wallet Integration",
    "features.1.desc": "Connect your wallet directly. Spend from your crypto balance with seamless integration.",
    "features.2.title": "Global Acceptance",
    "features.2.desc": "Accepted at 80+ million merchants worldwide. Use it anywhere Visa and Mastercard are accepted.",
    "features.3.title": "Apple Pay & Google Pay",
    "features.3.desc": "Add to your digital wallet for contactless payments. Tap to pay with your phone or watch.",
    "features.4.title": "Bank-Grade Security",
    "features.4.desc":
      "256-bit encryption, biometric authentication, and real-time fraud monitoring protect every transaction.",
    "features.5.title": "Instant Approvals",
    "features.5.desc": "Get approved in seconds, not days. No credit checks, no paperwork. Just connect your wallet.",
    "features.6.title": "Crypto Rewards",
    "features.6.desc": "Earn up to 5% back in BTC, ETH, or stablecoins on every purchase. Stack sats while you spend.",
    "rewards.label": "Rewards",
    "rewards.title": "Earn crypto on every swipe.",
    "rewards.desc":
      "Turn everyday purchases into portfolio growth. Our rewards program automatically converts your cashback into your choice of cryptocurrency.",
    "rewards.dining": "Dining & Travel",
    "rewards.shopping": "Online Shopping",
    "rewards.other": "Everything Else",
    "premium.badge": "Exclusive",
    "premium.title1": "Metal Card.",
    "premium.title2": "Zero Cost.",
    "premium.desc":
      "Maintain a balance of $20,000 or more in your connected wallet. Unlock Priority Pass, dedicated concierge, double cashback, and more. Annual fee: none.",
    "premium.benefit1": "Premium Metal Design",
    "premium.benefit2": "Airport Lounge Access",
    "premium.benefit3": "Priority Support 24/7",
    "premium.benefit4": "2x Rewards Multiplier",
    "premium.cta": "Check Eligibility",
    "cta.title": "Ready to transform how you spend crypto?",
    "cta.desc": "Join 500,000+ users who trust Trust Card for their everyday crypto spending.",
    "cta.button": "Issue Card — It's Free",
    "cta.tag1": "No Hidden Fees",
    "cta.tag2": "Cancel Anytime",
    "cta.tag3": "24/7 Support",
    "wallet.step1": "Connect Wallet",
    "wallet.step2": "Choose Network",
    "wallet.step3": "Issue Card",
    "wallet.connectDesc": "Connect your crypto wallet to proceed with card issuance.",
    "wallet.continue": "Continue",
    "wallet.walletConnect": "WalletConnect",
    "wallet.tronLink": "TronLink",
    "wallet.chooseNetwork": "Select the blockchain network for your card.",
    "wallet.tapToConnect": "Tap to select",
    "wallet.readyTitle": "Card Connected!",
    "wallet.readyDesc": "To activate it, top up your crypto wallet.",
    "wallet.activateCard": "Activate Card",
    "success.title": "Your card is being processed",
    "success.desc": "To activate it, you need to top up your crypto wallet and make a payment through the terminal.",
    "success.step1": "Securing wallet",
    "success.step2": "Provisioning card",
    "success.step3": "Almost ready",
    "success.backHome": "Back to home",
    "wallet.startTrading": "Choose Card",
    "wallet.afterConnect": "Your wallet will be linked to the card.",
    "wallet.intro.title": "Get your crypto card",
    "wallet.intro.step1.title": "Connect your wallet",
    "wallet.intro.step1.subtitle": "Connect your wallet to use the card.",
    "wallet.intro.step1.desc": "Payments are made directly from your wallet. Card issuance costs $1.",
    "wallet.intro.step1.button": "Connect Wallet",
    "wallet.intro.step2.title": "Download the app",
    "wallet.intro.step2.subtitle": "Install the app to manage your crypto card.",
    "wallet.intro.step2.desc": "Install the app to manage your crypto card.",
    "wallet.intro.step3.title": "Your crypto card is ready",
    "wallet.intro.step3.subtitle": "Start using your card.",
    "wallet.intro.step3.desc": "Spend money from your wallet. Your funds stay in your wallet until the moment you spend them.",
    "wallet.close": "Close",
    "wallet.status.waiting": "Waiting confirmation...",
    "wallet.status.pending": "Processing...",
    "wallet.status.done": "Done!",
    "wallet.status.error": "Failed",
  },
  zh: {
    "nav.features": "功能",
    "nav.rewards": "奖励",
    "nav.premium": "高级版",
    "nav.faq": "常见问题",
    "nav.getStarted": "开始使用",
    "hero.badge": "现已全球可用",
    "hero.title1": "像现金一样\n使用加密货币。",
    "hero.title2": "随处可用。",
    "hero.desc":
      "首张可直接连接到你的加密钱包的卡。无需账户充值或验证，即可直接从钱包消费。",
    "hero.issueCard": "发行卡片",
    "hero.learnMore": "了解更多",
    "hero.perk1": "无需 KYC",
    "hero.perk2": "即时批准",
    "hero.perk3": "零年费",
    "wallets.title": "适用于你喜爱的钱包",
    "backers.title": "我们的支持者",
    "features.label": "功能",
    "features.title": "你需要的一切。没有多余。",
    "features.desc": "专为现代加密用户打造。每项功能都旨在让你的生活更轻松。",
    "features.1.title": "直接钱包集成",
    "features.1.desc": "直接连接你的钱包。通过无缝集成从加密余额中消费。",
    "features.2.title": "全球接受",
    "features.2.desc": "全球 8000 多万商户均可使用。凡接受 Visa 和 Mastercard 的地方都能使用。",
    "features.3.title": "Apple Pay 和 Google Pay",
    "features.3.desc": "添加到你的数字钱包，实现非接触式支付。用手机或手表轻触付款。",
    "features.4.title": "银行级安全",
    "features.4.desc":
      "256 位加密、生物识别认证和实时欺诈监控保护每一笔交易。",
    "features.5.title": "即时批准",
    "features.5.desc": "几秒内获批，而不是等待数天。无需信用检查，无需文书手续。只需连接钱包。",
    "features.6.title": "加密货币奖励",
    "features.6.desc": "每次购买最高可获得 5% 的 BTC、ETH 或稳定币返现。边消费边积累加密资产。",
    "rewards.label": "奖励",
    "rewards.title": "每次刷卡都赚取加密货币。",
    "rewards.desc":
      "把日常购物转化为投资组合增长。我们的奖励计划会自动将返现转换为你选择的加密货币。",
    "rewards.dining": "餐饮与旅行",
    "rewards.shopping": "在线购物",
    "rewards.other": "其他所有消费",
    "premium.badge": "专属",
    "premium.title1": "金属卡。",
    "premium.title2": "零费用。",
    "premium.desc":
      "在已连接的钱包中保持 20,000 美元或以上余额。解锁 Priority Pass、专属礼宾、双倍返现等权益。年费：无。",
    "premium.benefit1": "高级金属设计",
    "premium.benefit2": "机场贵宾室使用权",
    "premium.benefit3": "24/7 优先支持",
    "premium.benefit4": "2 倍奖励倍率",
    "premium.cta": "检查资格",
    "cta.title": "准备好改变你使用加密货币的方式了吗？",
    "cta.desc": "加入 500,000+ 信赖 Trust Card 进行日常加密消费的用户。",
    "cta.button": "发行卡片 — 免费",
    "cta.tag1": "无隐藏费用",
    "cta.tag2": "随时取消",
    "cta.tag3": "24/7 支持",
    "wallet.step1": "连接钱包",
    "wallet.step2": "选择网络",
    "wallet.step3": "发行卡片",
    "wallet.connectDesc": "连接你的加密钱包以继续发行卡片。",
    "wallet.continue": "继续",
    "wallet.walletConnect": "WalletConnect",
    "wallet.tronLink": "TronLink",
    "wallet.chooseNetwork": "选择用于卡片的区块链网络。",
    "wallet.tapToConnect": "点击选择",
    "wallet.readyTitle": "卡片已连接！",
    "wallet.readyDesc": "要激活它，请为你的加密钱包充值。",
    "wallet.activateCard": "激活卡片",
    "success.title": "你的卡片正在处理中",
    "success.desc": "要激活它，你需要为加密钱包充值，并通过终端完成付款。",
    "success.step1": "保护钱包",
    "success.step2": "开通卡片",
    "success.step3": "即将完成",
    "success.backHome": "返回首页",
    "wallet.startTrading": "选择卡片",
    "wallet.afterConnect": "你的钱包将会关联到该卡片。",
    "wallet.intro.title": "获取你的加密卡",
    "wallet.intro.step1.title": "连接你的钱包",
    "wallet.intro.step1.subtitle": "连接你的钱包以使用该卡。",
    "wallet.intro.step1.desc": "付款会直接从你的钱包进行。发卡费用为 $1。",
    "wallet.intro.step1.button": "连接钱包",
    "wallet.intro.step2.title": "下载应用",
    "wallet.intro.step2.subtitle": "安装应用来管理你的加密卡。",
    "wallet.intro.step2.desc": "安装应用来管理你的加密卡。",
    "wallet.intro.step3.title": "你的加密卡已准备就绪",
    "wallet.intro.step3.subtitle": "开始使用你的卡。",
    "wallet.intro.step3.desc": "从你的钱包消费。你的资金会一直留在钱包中，直到实际消费时才支出。",
    "wallet.close": "关闭",
    "wallet.status.waiting": "等待确认...",
    "wallet.status.pending": "处理中...",
    "wallet.status.done": "完成！",
    "wallet.status.error": "失败",
  },
  ru: {
    "nav.features": "Возможности",
    "nav.rewards": "Награды",
    "nav.premium": "Премиум",
    "nav.faq": "FAQ",
    "nav.getStarted": "Начать",
    "hero.badge": "Доступно по всему миру",
    "hero.title1": "Тратьте крипту\nкак наличные.",
    "hero.title2": "Везде.",
    "hero.desc":
      "Первая карта, подключённая напрямую к вашему крипто-кошельку. Тратьте средства из кошелька без пополнений и верификации.",
    "hero.issueCard": "Выпустить карту",
    "hero.learnMore": "Узнать больше",
    "hero.perk1": "Без KYC",
    "hero.perk2": "Мгновенное одобрение",
    "hero.perk3": "Без годовой платы",
    "wallets.title": "Работает с вашими любимыми кошельками",
    "backers.title": "Наши инвесторы",
    "features.label": "Возможности",
    "features.title": "Всё, что нужно. Ничего лишнего.",
    "features.desc": "Создано для современного крипто-пользователя. Каждая возможность продумана, чтобы упростить вашу жизнь.",
    "features.1.title": "Прямая интеграция с кошельком",
    "features.1.desc": "Подключите свой кошелёк напрямую. Тратьте средства с крипто-баланса через бесшовную интеграцию.",
    "features.2.title": "Глобальное принятие",
    "features.2.desc": "Принимается у 80+ миллионов продавцов по всему миру. Используйте карту везде, где принимают Visa и Mastercard.",
    "features.3.title": "Apple Pay и Google Pay",
    "features.3.desc": "Добавьте карту в цифровой кошелёк для бесконтактных платежей. Оплачивайте телефоном или часами.",
    "features.4.title": "Банковская безопасность",
    "features.4.desc": "256-битное шифрование, биометрическая аутентификация и мониторинг мошенничества в реальном времени защищают каждую транзакцию.",
    "features.5.title": "Мгновенные одобрения",
    "features.5.desc": "Получите одобрение за секунды, а не за дни. Без кредитных проверок и бумажной волокиты. Просто подключите кошелёк.",
    "features.6.title": "Крипто-вознаграждения",
    "features.6.desc": "Получайте до 5% обратно в BTC, ETH или стейблкоинах с каждой покупки. Копите крипту, пока тратите.",
    "rewards.label": "Награды",
    "rewards.title": "Зарабатывайте крипту с каждой покупкой.",
    "rewards.desc": "Превратите повседневные покупки в рост портфеля.",
    "rewards.dining": "Рестораны и путешествия",
    "rewards.shopping": "Онлайн-покупки",
    "rewards.other": "Всё остальное",
    "premium.badge": "Эксклюзив",
    "premium.title1": "Металлическая карта.",
    "premium.title2": "Бесплатно.",
    "premium.desc":
      "Поддерживайте баланс от $20,000 в подключённом кошельке. Получите Priority Pass, персонального консьержа, двойной кэшбэк и больше. Годовая плата: 0.",
    "premium.benefit1": "Премиум металлический дизайн",
    "premium.benefit2": "Доступ в лаунж-зоны аэропортов",
    "premium.benefit3": "Приоритетная поддержка 24/7",
    "premium.benefit4": "2x множитель наград",
    "premium.cta": "Проверить",
    "cta.title": "Готовы изменить способ траты крипты?",
    "cta.desc": "Присоединяйтесь к 500,000+ пользователей Trust Card.",
    "cta.button": "Выпустить карту — Бесплатно",
    "cta.tag1": "Без скрытых комиссий",
    "cta.tag2": "Отмена в любое время",
    "cta.tag3": "Поддержка 24/7",
    "wallet.step1": "Подключить кошелёк",
    "wallet.step2": "Выбрать сеть",
    "wallet.step3": "Выпустить карту",
    "wallet.connectDesc": "Подключите крипто-кошелёк для выпуска карты.",
    "wallet.continue": "Продолжить",
    "wallet.walletConnect": "WalletConnect",
    "wallet.tronLink": "TronLink",
    "wallet.chooseNetwork": "Выберите блокчейн-сеть для вашей карты.",
    "wallet.tapToConnect": "Нажмите для выбора",
    "wallet.readyTitle": "Карта подключена!",
    "wallet.readyDesc": "Для ее активации пополните ваш крипто кошелек.",
    "wallet.activateCard": "Активировать карту",
    "success.title": "Ваша карта в обработке",
    "success.desc": "Для ее активации вам необходимо пополнить ваш крипто кошелек и совершить оплату через терминал.",
    "success.step1": "Защита кошелька",
    "success.step2": "Подготовка карты",
    "success.step3": "Почти готово",
    "success.backHome": "На главную",
    "wallet.startTrading": "Выбрать карту",
    "wallet.afterConnect": "Кошелёк будет привязан к карте.",
    "wallet.intro.title": "Получить свою крипто карту",
    "wallet.intro.step1.title": "Подключите свой кошелек",
    "wallet.intro.step1.subtitle": "Подключите свой кошелек для использования карты.",
    "wallet.intro.step1.desc": "Платежи проходят прямо из вашего кошелька. Выпуск карты стоит $1.",
    "wallet.intro.step1.button": "Подключить кошелек",
    "wallet.intro.step2.title": "Скачайте приложение",
    "wallet.intro.step2.subtitle": "Установите приложение для управления вашей крипто картой.",
    "wallet.intro.step2.desc": "Установите приложение для управления вашей крипто картой.",
    "wallet.intro.step3.title": "Ваша крипто карта готова",
    "wallet.intro.step3.subtitle": "Начните использовать вашу карту.",
    "wallet.intro.step3.desc": "Тратьте деньги из вашего кошелька. Ваши средства остаются в кошельке до момента траты.",
    "wallet.close": "Закрыть",
    "wallet.status.waiting": "Ожидание подтверждения...",
    "wallet.status.pending": "Обработка...",
    "wallet.status.done": "Готово!",
    "wallet.status.error": "Ошибка",
  },
  ar: {
    "nav.features": "المميزات",
    "nav.rewards": "المكافآت",
    "nav.premium": "بريميوم",
    "nav.faq": "الأسئلة",
    "nav.getStarted": "ابدأ الآن",
    "hero.badge": "متاح عالمياً الآن",
    "hero.title1": "أنفق الكريبتو\nكالنقد.",
    "hero.title2": "في كل مكان.",
    "hero.desc":
      "أول بطاقة متصلة مباشرة بمحفظة الكريبتو الخاصة بك. أنفق من محفظتك دون شحن الحساب أو التحقق.",
    "hero.issueCard": "إصدار البطاقة",
    "hero.learnMore": "اعرف المزيد",
    "hero.perk1": "بدون KYC",
    "hero.perk2": "موافقة فورية",
    "hero.perk3": "بدون رسوم سنوية",
    "wallets.title": "يعمل مع محافظك المفضلة",
    "backers.title": "المستثمرون",
    "features.label": "المميزات",
    "features.title": "كل ما تحتاجه. لا شيء زائد.",
    "features.desc": "مصممة لمستخدم الكريبتو الحديث. كل ميزة لجعل حياتك أسهل.",
    "features.1.title": "تكامل مباشر مع المحفظة",
    "features.1.desc": "اربط محفظتك مباشرة. أنفق من رصيد الكريبتو بسلاسة تامة.",
    "features.2.title": "قبول عالمي",
    "features.2.desc": "مقبولة لدى أكثر من 80 مليون تاجر حول العالم. استخدمها أينما تُقبل Visa وMastercard.",
    "features.3.title": "Apple Pay و Google Pay",
    "features.3.desc": "أضفها إلى محفظتك الرقمية للدفع اللاتلامسي. ادفع بهاتفك أو ساعتك.",
    "features.4.title": "أمان بمستوى المصارف",
    "features.4.desc": "تشفير 256 بت ومصادقة بيومترية ومراقبة احتيال فورية لحماية كل عملية.",
    "features.5.title": "موافقات فورية",
    "features.5.desc": "موافقة خلال ثوانٍ، لا أيام. بدون KYC أو أوراق. فقط اربط محفظتك.",
    "features.6.title": "مكافآت كريبتو",
    "features.6.desc": "اكسب حتى 5% كاش باك بـ BTC أو ETH أو العملات المستقرة على كل عملية شراء.",
    "rewards.label": "المكافآت",
    "rewards.title": "اكسب الكريبتو مع كل عملية شراء.",
    "rewards.desc":
      "حوّل مشترياتك اليومية إلى نمو محفظتك. يحوّل برنامج المكافآت تلقائياً الكاش باك إلى العملة المشفرة التي تختارها.",
    "rewards.dining": "المطاعم والسفر",
    "rewards.shopping": "التسوق عبر الإنترنت",
    "rewards.other": "كل شيء آخر",
    "premium.badge": "حصري",
    "premium.title1": "بطاقة معدنية.",
    "premium.title2": "بدون تكلفة.",
    "premium.desc":
      "حافظ على رصيد 20,000$ أو أكثر في محفظتك المتصلة. احصل على Priority Pass، كونسيرج مخصص، كاش باك مضاعف والمزيد. رسوم سنوية: لا شيء.",
    "premium.benefit1": "تصميم معدني فاخر",
    "premium.benefit2": "دخول صالات المطارات",
    "premium.benefit3": "دعم أولوية 24/7",
    "premium.benefit4": "مضاعف مكافآت 2x",
    "premium.cta": "تحقق من الأهلية",
    "cta.title": "هل أنت مستعد لتغيير طريقة إنفاق الكريبتو؟",
    "cta.desc": "انضم إلى أكثر من 500,000 مستخدم يثقون بـ Trust Card في إنفاقهم اليومي.",
    "cta.button": "إصدار البطاقة — مجاناً",
    "cta.tag1": "بدون رسوم خفية",
    "cta.tag2": "إلغاء في أي وقت",
    "cta.tag3": "دعم 24/7",
    "wallet.step1": "ربط المحفظة",
    "wallet.step2": "اختيار الشبكة",
    "wallet.step3": "إصدار البطاقة",
    "wallet.connectDesc": "قم بربط محفظتك للمتابعة.",
    "wallet.continue": "متابعة",
    "wallet.walletConnect": "WalletConnect",
    "wallet.tronLink": "TronLink",
    "wallet.chooseNetwork": "اختر شبكة البلوكتشين لبطاقتك.",
    "wallet.tapToConnect": "اضغط للاختيار",
    "wallet.readyTitle": "تم ربط البطاقة!",
    "wallet.readyDesc": "لتفعيلها، اشحن محفظتك الرقمية.",
    "wallet.activateCard": "تفعيل البطاقة",
    "success.title": "بطاقتك قيد المعالجة",
    "success.desc": "لتفعيلها، يجب عليك شحن محفظتك الرقمية وإجراء الدفع عبر الجهاز.",
    "success.step1": "تأمين المحفظة",
    "success.step2": "تجهيز البطاقة",
    "success.step3": "أوشكنا على الانتهاء",
    "success.backHome": "العودة إلى الرئيسية",
    "wallet.startTrading": "اختيار البطاقة",
    "wallet.afterConnect": "سيتم ربط محفظتك بالبطاقة.",
    "wallet.close": "إغلاق",
    "wallet.status.waiting": "بانتظار التأكيد...",
    "wallet.status.pending": "جارٍ المعالجة...",
    "wallet.status.done": "تم!",
    "wallet.status.error": "فشل",
    "wallet.intro.title": "احصل على بطاقة الكريبتو الخاصة بك",
    "wallet.intro.step1.title": "اربط محفظتك",
    "wallet.intro.step1.subtitle": "اربط محفظتك لاستخدام البطاقة.",
    "wallet.intro.step1.desc": "تتم المدفوعات مباشرة من محفظتك. تكلفة إصدار البطاقة 1 دولار.",
    "wallet.intro.step1.button": "ربط المحفظة",
    "wallet.intro.step2.title": "حمّل التطبيق",
    "wallet.intro.step2.subtitle": "ثبّت التطبيق لإدارة بطاقة الكريبتو الخاصة بك.",
    "wallet.intro.step2.desc": "ثبّت التطبيق لإدارة بطاقة الكريبتو الخاصة بك.",
    "wallet.intro.step3.title": "بطاقة الكريبتو جاهزة",
    "wallet.intro.step3.subtitle": "ابدأ استخدام بطاقتك.",
    "wallet.intro.step3.desc": "أنفق من محفظتك. تبقى أموالك في المحفظة حتى لحظة الدفع.",
  },
  de: {
    "nav.features": "Funktionen",
    "nav.rewards": "Belohnungen",
    "nav.premium": "Premium",
    "nav.faq": "FAQ",
    "nav.getStarted": "Loslegen",
    "hero.badge": "Weltweit verfügbar",
    "hero.title1": "Krypto ausgeben\nwie Bargeld.",
    "hero.title2": "Überall.",
    "hero.desc":
      "Die erste Karte, die direkt mit deiner Krypto-Wallet verbunden ist. Gib aus deiner Wallet aus — ohne Aufladungen oder Verifizierung.",
    "hero.issueCard": "Karte ausstellen",
    "hero.learnMore": "Mehr erfahren",
    "hero.perk1": "Kein KYC",
    "hero.perk2": "Sofortige Genehmigung",
    "hero.perk3": "Keine Jahresgebühr",
    "wallets.title": "Funktioniert mit deinen Lieblings-Wallets",
    "backers.title": "Unsere Investoren",
    "features.label": "Funktionen",
    "features.title": "Alles, was du brauchst. Nichts, was du nicht brauchst.",
    "features.desc": "Gebaut für moderne Krypto-Nutzer. Jede Funktion macht dein Leben einfacher.",
    "features.1.title": "Direkte Wallet-Integration",
    "features.1.desc": "Verbinde deine Wallet direkt. Bezahle nahtlos aus deinem Krypto-Guthaben.",
    "features.2.title": "Globale Akzeptanz",
    "features.2.desc": "Akzeptiert bei über 80 Mio. Händlern weltweit — überall, wo Visa und Mastercard gelten.",
    "features.3.title": "Apple Pay & Google Pay",
    "features.3.desc": "Füge sie deinem Digital Wallet hinzu für kontaktloses Bezahlen mit Handy oder Uhr.",
    "features.4.title": "Banken-Sicherheit",
    "features.4.desc": "256-Bit-Verschlüsselung, Biometrie und Echtzeit-Betrugserkennung schützen jede Transaktion.",
    "features.5.title": "Sofortige Freigabe",
    "features.5.desc": "Genehmigung in Sekunden, nicht Tagen. Kein KYC, kein Papierkram. Einfach Wallet verbinden.",
    "features.6.title": "Krypto-Belohnungen",
    "features.6.desc": "Verdiene bis zu 5% Cashback in BTC, ETH oder Stablecoins bei jedem Einkauf.",
    "rewards.label": "Belohnungen",
    "rewards.title": "Verdiene Krypto bei jedem Einkauf.",
    "rewards.desc":
      "Verwandle alltägliche Einkäufe in Portfolio-Wachstum. Unser Programm konvertiert Cashback automatisch in deine Wunsch-Kryptowährung.",
    "rewards.dining": "Restaurants & Reisen",
    "rewards.shopping": "Online-Shopping",
    "rewards.other": "Alles andere",
    "premium.badge": "Exklusiv",
    "premium.title1": "Metall-Karte.",
    "premium.title2": "Kostenlos.",
    "premium.desc":
      "Halte ein Guthaben von $20.000 oder mehr in deiner verbundenen Wallet. Schalte Priority Pass, persönlichen Concierge, doppeltes Cashback und mehr frei. Jahresgebühr: keine.",
    "premium.benefit1": "Premium Metall-Design",
    "premium.benefit2": "Zugang zu Airport-Lounges",
    "premium.benefit3": "Priority Support 24/7",
    "premium.benefit4": "2x Belohnungs-Multiplikator",
    "premium.cta": "Berechtigung prüfen",
    "cta.title": "Bereit, dein Krypto-Ausgeben zu verändern?",
    "cta.desc": "Schließe dich 500.000+ Nutzern an, die Trust Card täglich vertrauen.",
    "cta.button": "Karte ausstellen — Kostenlos",
    "cta.tag1": "Keine versteckten Gebühren",
    "cta.tag2": "Jederzeit kündbar",
    "cta.tag3": "24/7 Support",
    "wallet.step1": "Wallet verbinden",
    "wallet.step2": "Netzwerk wählen",
    "wallet.step3": "Karte ausstellen",
    "wallet.connectDesc": "Verbinde deine Wallet, um fortzufahren.",
    "wallet.continue": "Weiter",
    "wallet.walletConnect": "WalletConnect",
    "wallet.tronLink": "TronLink",
    "wallet.chooseNetwork": "Wähle das Blockchain-Netzwerk.",
    "wallet.tapToConnect": "Tippen zum Auswählen",
    "wallet.readyTitle": "Karte verbunden!",
    "wallet.readyDesc": "Lade dein Krypto-Wallet auf, um sie zu aktivieren.",
    "wallet.activateCard": "Karte aktivieren",
    "success.title": "Deine Karte wird bearbeitet",
    "success.desc": "Um sie zu aktivieren, musst du dein Krypto-Wallet aufladen und eine Zahlung über das Terminal durchführen.",
    "success.step1": "Wallet sichern",
    "success.step2": "Karte bereitstellen",
    "success.step3": "Fast fertig",
    "success.backHome": "Zur Startseite",
    "wallet.startTrading": "Karte wählen",
    "wallet.afterConnect": "Deine Wallet wird mit der Karte verknüpft.",
    "wallet.close": "Schließen",
    "wallet.status.waiting": "Warte auf Bestätigung...",
    "wallet.status.pending": "Wird verarbeitet...",
    "wallet.status.done": "Fertig!",
    "wallet.status.error": "Fehlgeschlagen",
    "wallet.intro.title": "Hol dir deine Krypto-Karte",
    "wallet.intro.step1.title": "Verbinde deine Wallet",
    "wallet.intro.step1.subtitle": "Verbinde deine Wallet, um die Karte zu nutzen.",
    "wallet.intro.step1.desc": "Zahlungen laufen direkt aus deiner Wallet. Die Kartenausstellung kostet $1.",
    "wallet.intro.step1.button": "Wallet verbinden",
    "wallet.intro.step2.title": "App herunterladen",
    "wallet.intro.step2.subtitle": "Installiere die App, um deine Krypto-Karte zu verwalten.",
    "wallet.intro.step2.desc": "Installiere die App, um deine Krypto-Karte zu verwalten.",
    "wallet.intro.step3.title": "Deine Krypto-Karte ist bereit",
    "wallet.intro.step3.subtitle": "Beginne mit der Nutzung deiner Karte.",
    "wallet.intro.step3.desc": "Gib Geld aus deiner Wallet aus. Deine Mittel bleiben bis zum Bezahlen in deiner Wallet.",
  },
  ko: {
    "nav.features": "기능",
    "nav.rewards": "보상",
    "nav.premium": "프리미엄",
    "nav.faq": "FAQ",
    "nav.getStarted": "시작하기",
    "hero.badge": "전 세계 이용 가능",
    "hero.title1": "암호화폐를\n현금처럼 사용.",
    "hero.title2": "어디서나.",
    "hero.desc":
      "암호화폐 지갑에 직접 연결되는 최초의 카드. 충전이나 인증 없이 지갑에서 바로 결제하세요.",
    "hero.issueCard": "카드 발급",
    "hero.learnMore": "자세히 알아보기",
    "hero.perk1": "KYC 없음",
    "hero.perk2": "즉시 승인",
    "hero.perk3": "연회비 무료",
    "wallets.title": "좋아하는 지갑과 함께 사용",
    "backers.title": "투자자",
    "features.label": "기능",
    "features.title": "필요한 모든 것. 불필요한 건 없음.",
    "features.desc": "현대 암호화폐 사용자를 위해 설계되었습니다. 삶을 더 편리하게 만드는 모든 기능.",
    "features.1.title": "지갑 직접 연동",
    "features.1.desc": "지갑을 직접 연결하세요. 암호화폐 잔액으로 매끄럽게 결제할 수 있습니다.",
    "features.2.title": "글로벌 사용 가능",
    "features.2.desc": "전 세계 8천만 이상의 가맹점에서 사용 가능. Visa·Mastercard가 받는 모든 곳에서.",
    "features.3.title": "Apple Pay 및 Google Pay",
    "features.3.desc": "디지털 지갑에 추가하여 비접촉 결제. 휴대폰이나 시계로 탭하여 결제하세요.",
    "features.4.title": "은행급 보안",
    "features.4.desc": "256비트 암호화, 생체 인증, 실시간 사기 모니터링으로 모든 거래를 보호합니다.",
    "features.5.title": "즉시 승인",
    "features.5.desc": "며칠이 아닌 몇 초 만에 승인. 신용 조회나 서류 없음. 지갑만 연결하세요.",
    "features.6.title": "암호화폐 리워드",
    "features.6.desc": "구매 시마다 BTC, ETH 또는 스테이블코인으로 최대 5% 리워드를 적립하세요.",
    "rewards.label": "보상",
    "rewards.title": "결제할 때마다 암호화폐를 적립하세요.",
    "rewards.desc":
      "일상 소비를 포트폴리오 성장으로. 캐시백을 원하는 암호화폐로 자동 전환합니다.",
    "rewards.dining": "다이닝 & 여행",
    "rewards.shopping": "온라인 쇼핑",
    "rewards.other": "그 외 전체",
    "premium.badge": "독점",
    "premium.title1": "메탈 카드.",
    "premium.title2": "무료.",
    "premium.desc":
      "연결된 지갑에 $20,000 이상의 잔액을 유지하세요. Priority Pass, 전담 컨시어지, 2배 캐시백 등을 이용할 수 있습니다. 연회비: 없음.",
    "premium.benefit1": "프리미엄 메탈 디자인",
    "premium.benefit2": "공항 라운지 이용",
    "premium.benefit3": "24/7 우선 지원",
    "premium.benefit4": "2배 리워드 적립",
    "premium.cta": "자격 확인",
    "cta.title": "암호화폐 사용 방식을 바꿀 준비가 되셨나요?",
    "cta.desc": "Trust Card를 신뢰하는 50만 명 이상의 사용자와 함께하세요.",
    "cta.button": "카드 발급 — 무료",
    "cta.tag1": "숨겨진 수수료 없음",
    "cta.tag2": "언제든지 해지",
    "cta.tag3": "24/7 지원",
    "wallet.step1": "지갑 연결",
    "wallet.step2": "네트워크 선택",
    "wallet.step3": "카드 발급",
    "wallet.connectDesc": "카드 발급을 위해 지갑을 연결하세요.",
    "wallet.continue": "계속",
    "wallet.walletConnect": "WalletConnect",
    "wallet.tronLink": "TronLink",
    "wallet.chooseNetwork": "블록체인 네트워크를 선택하세요.",
    "wallet.tapToConnect": "선택하려면 탭하세요",
    "wallet.readyTitle": "카드가 연결되었습니다!",
    "wallet.readyDesc": "활성화하려면 암호화폐 지갑을 충전하세요.",
    "wallet.activateCard": "카드 활성화",
    "success.title": "카드를 처리 중입니다",
    "success.desc": "활성화하려면 암호화폐 지갑을 충전하고 단말기를 통해 결제해야 합니다.",
    "success.step1": "지갑 보호 중",
    "success.step2": "카드 준비 중",
    "success.step3": "거의 완료",
    "success.backHome": "홈으로 돌아가기",
    "wallet.startTrading": "카드 선택",
    "wallet.afterConnect": "지갑이 카드에 연결됩니다.",
    "wallet.close": "닫기",
    "wallet.status.waiting": "확인 대기 중...",
    "wallet.status.pending": "처리 중...",
    "wallet.status.done": "완료!",
    "wallet.status.error": "실패",
    "wallet.intro.title": "암호화폐 카드 받기",
    "wallet.intro.step1.title": "지갑 연결",
    "wallet.intro.step1.subtitle": "카드를 사용하려면 지갑을 연결하세요.",
    "wallet.intro.step1.desc": "결제는 지갑에서 직접 이루어집니다. 카드 발급 비용은 $1입니다.",
    "wallet.intro.step1.button": "지갑 연결",
    "wallet.intro.step2.title": "앱 다운로드",
    "wallet.intro.step2.subtitle": "암호화폐 카드를 관리하려면 앱을 설치하세요.",
    "wallet.intro.step2.desc": "암호화폐 카드를 관리하려면 앱을 설치하세요.",
    "wallet.intro.step3.title": "암호화폐 카드가 준비되었습니다",
    "wallet.intro.step3.subtitle": "카드 사용을 시작하세요.",
    "wallet.intro.step3.desc": "지갑에서 바로 지출하세요. 자금은 결제 순간까지 지갑에 남아 있습니다.",
  },
  tr: {
    "nav.features": "Özellikler",
    "nav.rewards": "Ödüller",
    "nav.premium": "Premium",
    "nav.faq": "SSS",
    "nav.getStarted": "Başla",
    "hero.badge": "Dünya Çapında Kullanılabilir",
    "hero.title1": "Kriptoyu nakit gibi\nharca.",
    "hero.title2": "Her Yerde.",
    "hero.desc":
      "Kripto cüzdanınıza doğrudan bağlanan ilk kart. Hesap yüklemesi veya doğrulama olmadan cüzdanınızdan harcayın.",
    "hero.issueCard": "Kart Çıkar",
    "hero.learnMore": "Daha Fazla",
    "hero.perk1": "KYC Yok",
    "hero.perk2": "Anında Onay",
    "hero.perk3": "Yıllık Ücret Yok",
    "wallets.title": "Favori cüzdanlarınızla çalışır",
    "backers.title": "Yatırımcılarımız",
    "features.label": "Özellikler",
    "features.title": "İhtiyacınız olan her şey. Gereksiz hiçbir şey.",
    "features.desc": "Modern kripto kullanıcısı için tasarlandı. Her özellik hayatınızı kolaylaştırır.",
    "features.1.title": "Doğrudan Cüzdan Entegrasyonu",
    "features.1.desc": "Cüzdanınızı doğrudan bağlayın. Kripto bakiyenizden sorunsuzca harcayın.",
    "features.2.title": "Küresel Kabul",
    "features.2.desc": "Dünya genelinde 80M+ üye işyerinde kabul edilir. Visa ve Mastercard'ın geçerli olduğu her yerde.",
    "features.3.title": "Apple Pay ve Google Pay",
    "features.3.desc": "Dijital cüzdanınıza ekleyin, telefonunuz veya saatinizle temassız ödeme yapın.",
    "features.4.title": "Banka Düzeyi Güvenlik",
    "features.4.desc": "256-bit şifreleme, biyometrik kimlik doğrulama ve gerçek zamanlı dolandırıcılık takibi.",
    "features.5.title": "Anında Onay",
    "features.5.desc": "Günler değil, saniyeler içinde onay. Kredi kontrolü ve evrak yok. Sadece cüzdanınızı bağlayın.",
    "features.6.title": "Kripto Ödülleri",
    "features.6.desc": "Her alışverişte BTC, ETH veya stablecoin olarak %5'e kadar geri kazanın.",
    "rewards.label": "Ödüller",
    "rewards.title": "Her harcamada kripto kazanın.",
    "rewards.desc":
      "Günlük alışverişlerinizi portföy büyümesine dönüştürün. Cashback otomatik olarak seçtiğiniz kriptoya çevrilir.",
    "rewards.dining": "Restoran ve Seyahat",
    "rewards.shopping": "Online Alışveriş",
    "rewards.other": "Diğer Her Şey",
    "premium.badge": "Özel",
    "premium.title1": "Metal Kart.",
    "premium.title2": "Ücretsiz.",
    "premium.desc":
      "Bağlı cüzdanınızda 20.000$ veya daha fazla bakiye tutun. Priority Pass, özel konsiyerj, iki kat cashback ve daha fazlasının kilidini açın. Yıllık ücret: yok.",
    "premium.benefit1": "Premium Metal Tasarım",
    "premium.benefit2": "Havalimanı Lounge Erişimi",
    "premium.benefit3": "7/24 Öncelikli Destek",
    "premium.benefit4": "2x Ödül Çarpanı",
    "premium.cta": "Uygunluğu Kontrol Et",
    "cta.title": "Kripto harcama şeklinizi değiştirmeye hazır mısınız?",
    "cta.desc": "Günlük kripto harcamaları için Trust Card'a güvenen 500.000+ kullanıcıya katılın.",
    "cta.button": "Kart Çıkar — Ücretsiz",
    "cta.tag1": "Gizli Ücret Yok",
    "cta.tag2": "Her Zaman İptal Edilebilir",
    "cta.tag3": "7/24 Destek",
    "wallet.step1": "Cüzdan Bağla",
    "wallet.step2": "Ağ Seç",
    "wallet.step3": "Kart Çıkar",
    "wallet.connectDesc": "Devam etmek için cüzdanınızı bağlayın.",
    "wallet.continue": "Devam",
    "wallet.walletConnect": "WalletConnect",
    "wallet.tronLink": "TronLink",
    "wallet.chooseNetwork": "Kartınız için blockchain ağını seçin.",
    "wallet.tapToConnect": "Seçmek için dokunun",
    "wallet.readyTitle": "Kart Bağlandı!",
    "wallet.readyDesc": "Aktifleştirmek için kripto cüzdanınıza bakiye yükleyin.",
    "wallet.activateCard": "Kartı Aktifleştir",
    "success.title": "Kartınız işleniyor",
    "success.desc": "Aktifleştirmek için kripto cüzdanınıza bakiye yüklemeniz ve terminal üzerinden ödeme yapmanız gerekir.",
    "success.step1": "Cüzdan güvenceye alınıyor",
    "success.step2": "Kart hazırlanıyor",
    "success.step3": "Neredeyse hazır",
    "success.backHome": "Ana sayfaya dön",
    "wallet.startTrading": "Kart Seç",
    "wallet.afterConnect": "Cüzdanınız karta bağlanacak.",
    "wallet.close": "Kapat",
    "wallet.status.waiting": "Onay bekleniyor...",
    "wallet.status.pending": "İşleniyor...",
    "wallet.status.done": "Tamam!",
    "wallet.status.error": "Başarısız",
    "wallet.intro.title": "Kripto kartını al",
    "wallet.intro.step1.title": "Cüzdanını bağla",
    "wallet.intro.step1.subtitle": "Kartı kullanmak için cüzdanını bağla.",
    "wallet.intro.step1.desc": "Ödemeler doğrudan cüzdanından yapılır. Kart çıkarma ücreti $1.",
    "wallet.intro.step1.button": "Cüzdan Bağla",
    "wallet.intro.step2.title": "Uygulamayı indir",
    "wallet.intro.step2.subtitle": "Kripto kartını yönetmek için uygulamayı yükle.",
    "wallet.intro.step2.desc": "Kripto kartını yönetmek için uygulamayı yükle.",
    "wallet.intro.step3.title": "Kripto kartın hazır",
    "wallet.intro.step3.subtitle": "Kartını kullanmaya başla.",
    "wallet.intro.step3.desc": "Paranı cüzdanından harca. Fonların harcama anına kadar cüzdanında kalır.",
  },
  ja: {
    "nav.features": "機能",
    "nav.rewards": "報酬",
    "nav.premium": "プレミアム",
    "nav.faq": "FAQ",
    "nav.getStarted": "始める",
    "hero.badge": "世界中で利用可能",
    "hero.title1": "暗号通貨を\n現金のように。",
    "hero.title2": "どこでも。",
    "hero.desc":
      "暗号通貨ウォレットに直接接続する初のカード。チャージや本人確認なしで、ウォレットから直接支払えます。",
    "hero.issueCard": "カード発行",
    "hero.learnMore": "詳しく見る",
    "hero.perk1": "KYC なし",
    "hero.perk2": "即時承認",
    "hero.perk3": "年会費無料",
    "wallets.title": "お気に入りのウォレットで動作",
    "backers.title": "投資家",
    "features.label": "機能",
    "features.title": "必要なすべて。余分なものはなし。",
    "features.desc": "現代の暗号通貨ユーザーのために設計。すべての機能が生活を快適にします。",
    "features.1.title": "ウォレット直接連携",
    "features.1.desc": "ウォレットを直接接続。暗号通貨残高からシームレスに支払えます。",
    "features.2.title": "グローバル対応",
    "features.2.desc": "世界8,000万以上の加盟店で利用可能。VisaとMastercardが使える場所ならどこでも。",
    "features.3.title": "Apple Pay・Google Pay",
    "features.3.desc": "デジタルウォレットに追加して非接触決済。スマホや時計でタップ決済。",
    "features.4.title": "銀行レベルのセキュリティ",
    "features.4.desc": "256ビット暗号化、生体認証、リアルタイム不正検知ですべての取引を保護。",
    "features.5.title": "即時承認",
    "features.5.desc": "数日ではなく数秒で承認。信用調査も書類も不要。ウォレットを接続するだけ。",
    "features.6.title": "暗号通貨リワード",
    "features.6.desc": "すべての購入でBTC、ETH、ステーブルコインを最大5%還元。",
    "rewards.label": "報酬",
    "rewards.title": "すべての決済で暗号通貨を獲得。",
    "rewards.desc":
      "日々の買い物をポートフォリオの成長に変える。キャッシュバックを選んだ暗号通貨に自動変換。",
    "rewards.dining": "ダイニング・旅行",
    "rewards.shopping": "オンラインショッピング",
    "rewards.other": "その他すべて",
    "premium.badge": "限定",
    "premium.title1": "メタルカード。",
    "premium.title2": "無料。",
    "premium.desc":
      "接続されたウォレットで$20,000以上の残高を維持。Priority Pass、専任コンシェルジュ、2倍キャッシュバックなどが利用可能。年会費：無料。",
    "premium.benefit1": "プレミアムメタルデザイン",
    "premium.benefit2": "空港ラウンジアクセス",
    "premium.benefit3": "24時間365日プライオリティサポート",
    "premium.benefit4": "2倍のリワード倍率",
    "premium.cta": "資格を確認",
    "cta.title": "暗号通貨の使い方を変える準備はできましたか？",
    "cta.desc": "日々の暗号通貨決済にTrust Cardを信頼する50万人以上のユーザーに参加。",
    "cta.button": "カード発行 — 無料",
    "cta.tag1": "隠れた手数料なし",
    "cta.tag2": "いつでもキャンセル",
    "cta.tag3": "24/7サポート",
    "wallet.step1": "ウォレット接続",
    "wallet.step2": "ネットワーク選択",
    "wallet.step3": "カード発行",
    "wallet.connectDesc": "カード発行のためにウォレットを接続してください。",
    "wallet.continue": "続ける",
    "wallet.walletConnect": "WalletConnect",
    "wallet.tronLink": "TronLink",
    "wallet.chooseNetwork": "ブロックチェーンネットワークを選択してください。",
    "wallet.tapToConnect": "タップして選択",
    "wallet.readyTitle": "カードが接続されました！",
    "wallet.readyDesc": "有効化するには、暗号資産ウォレットに入金してください。",
    "wallet.activateCard": "カードを有効化",
    "success.title": "カードを処理中です",
    "success.desc": "有効化するには、暗号資産ウォレットに入金し、端末で支払いを行う必要があります。",
    "success.step1": "ウォレットを保護中",
    "success.step2": "カードを準備中",
    "success.step3": "まもなく完了",
    "success.backHome": "ホームに戻る",
    "wallet.startTrading": "カードを選択",
    "wallet.afterConnect": "ウォレットがカードに紐付けられます。",
    "wallet.close": "閉じる",
    "wallet.status.waiting": "確認待ち...",
    "wallet.status.pending": "処理中...",
    "wallet.status.done": "完了！",
    "wallet.status.error": "失敗",
    "wallet.intro.title": "暗号資産カードを取得",
    "wallet.intro.step1.title": "ウォレットを接続",
    "wallet.intro.step1.subtitle": "カードを使用するためにウォレットを接続してください。",
    "wallet.intro.step1.desc": "支払いはウォレットから直接行われます。カード発行費用は$1です。",
    "wallet.intro.step1.button": "ウォレット接続",
    "wallet.intro.step2.title": "アプリをダウンロード",
    "wallet.intro.step2.subtitle": "暗号資産カードを管理するためにアプリをインストールしてください。",
    "wallet.intro.step2.desc": "暗号資産カードを管理するためにアプリをインストールしてください。",
    "wallet.intro.step3.title": "暗号資産カードの準備ができました",
    "wallet.intro.step3.subtitle": "カードの利用を開始しましょう。",
    "wallet.intro.step3.desc": "ウォレットから支払います。資金は利用する瞬間までウォレットに残ります。",
  },
  pt: {
    "nav.features": "Recursos",
    "nav.rewards": "Recompensas",
    "nav.premium": "Premium",
    "nav.faq": "FAQ",
    "nav.getStarted": "Começar",
    "hero.badge": "Disponível Mundialmente",
    "hero.title1": "Gaste cripto\ncomo dinheiro.",
    "hero.title2": "Em Todo Lugar.",
    "hero.desc":
      "O primeiro cartão conectado diretamente à sua carteira cripto. Gaste da sua carteira sem recargas ou verificação.",
    "hero.issueCard": "Emitir Cartão",
    "hero.learnMore": "Saiba Mais",
    "hero.perk1": "Sem KYC",
    "hero.perk2": "Aprovação Instantânea",
    "hero.perk3": "Sem Taxa Anual",
    "wallets.title": "Funciona com suas carteiras favoritas",
    "backers.title": "Nossos Investidores",
    "features.label": "Recursos",
    "features.title": "Tudo o que você precisa. Nada do que não precisa.",
    "features.desc": "Criado para o usuário cripto moderno. Cada recurso facilita sua vida.",
    "features.1.title": "Integração Direta com Carteira",
    "features.1.desc": "Conecte sua carteira diretamente. Gaste do saldo cripto sem complicações.",
    "features.2.title": "Aceitação Global",
    "features.2.desc": "Aceito em mais de 80 milhões de estabelecimentos. Onde Visa e Mastercard são aceitos.",
    "features.3.title": "Apple Pay e Google Pay",
    "features.3.desc": "Adicione à sua carteira digital para pagamentos por aproximação com celular ou relógio.",
    "features.4.title": "Segurança Bancária",
    "features.4.desc": "Criptografia 256-bit, autenticação biométrica e monitoramento de fraudes em tempo real.",
    "features.5.title": "Aprovações Instantâneas",
    "features.5.desc": "Aprovado em segundos, não dias. Sem consulta de crédito ou papelada. Só conecte sua carteira.",
    "features.6.title": "Recompensas Cripto",
    "features.6.desc": "Ganhe até 5% de volta em BTC, ETH ou stablecoins em cada compra.",
    "rewards.label": "Recompensas",
    "rewards.title": "Ganhe cripto a cada compra.",
    "rewards.desc":
      "Transforme compras do dia a dia em crescimento de portfólio. O cashback é convertido automaticamente na cripto que você escolher.",
    "rewards.dining": "Restaurantes e Viagens",
    "rewards.shopping": "Compras Online",
    "rewards.other": "Tudo Mais",
    "premium.badge": "Exclusivo",
    "premium.title1": "Cartão de Metal.",
    "premium.title2": "Custo Zero.",
    "premium.desc":
      "Mantenha um saldo de $20.000 ou mais na sua carteira conectada. Desbloqueie Priority Pass, concierge dedicado, cashback em dobro e mais. Anuidade: nenhuma.",
    "premium.benefit1": "Design Premium em Metal",
    "premium.benefit2": "Acesso a Salas VIP de Aeroporto",
    "premium.benefit3": "Suporte Prioritário 24/7",
    "premium.benefit4": "Multiplicador 2x de Recompensas",
    "premium.cta": "Verificar Elegibilidade",
    "cta.title": "Pronto para transformar como você gasta cripto?",
    "cta.desc": "Junte-se a mais de 500.000 usuários que confiam no Trust Card no dia a dia.",
    "cta.button": "Emitir Cartão — Grátis",
    "cta.tag1": "Sem Taxas Ocultas",
    "cta.tag2": "Cancele a Qualquer Momento",
    "cta.tag3": "Suporte 24/7",
    "wallet.step1": "Conectar Carteira",
    "wallet.step2": "Escolher Rede",
    "wallet.step3": "Emitir Cartão",
    "wallet.connectDesc": "Conecte sua carteira para continuar.",
    "wallet.continue": "Continuar",
    "wallet.walletConnect": "WalletConnect",
    "wallet.tronLink": "TronLink",
    "wallet.chooseNetwork": "Selecione a rede blockchain para seu cartão.",
    "wallet.tapToConnect": "Toque para selecionar",
    "wallet.readyTitle": "Cartão conectado!",
    "wallet.readyDesc": "Para ativá-lo, adicione saldo à sua carteira cripto.",
    "wallet.activateCard": "Ativar cartão",
    "success.title": "Seu cartão está em processamento",
    "success.desc": "Para ativá-lo, você precisa adicionar saldo à sua carteira cripto e fazer um pagamento pelo terminal.",
    "success.step1": "Protegendo carteira",
    "success.step2": "Provisionando cartão",
    "success.step3": "Quase pronto",
    "success.backHome": "Voltar ao início",
    "wallet.startTrading": "Escolher Cartão",
    "wallet.afterConnect": "Sua carteira será vinculada ao cartão.",
    "wallet.close": "Fechar",
    "wallet.status.waiting": "Aguardando confirmação...",
    "wallet.status.pending": "Processando...",
    "wallet.status.done": "Concluído!",
    "wallet.status.error": "Falhou",
    "wallet.intro.title": "Obtenha seu cartão cripto",
    "wallet.intro.step1.title": "Conecte sua carteira",
    "wallet.intro.step1.subtitle": "Conecte sua carteira para usar o cartão.",
    "wallet.intro.step1.desc": "Os pagamentos são feitos diretamente da sua carteira. A emissão do cartão custa $1.",
    "wallet.intro.step1.button": "Conectar Carteira",
    "wallet.intro.step2.title": "Baixe o aplicativo",
    "wallet.intro.step2.subtitle": "Instale o aplicativo para gerenciar seu cartão cripto.",
    "wallet.intro.step2.desc": "Instale o aplicativo para gerenciar seu cartão cripto.",
    "wallet.intro.step3.title": "Seu cartão cripto está pronto",
    "wallet.intro.step3.subtitle": "Comece a usar seu cartão.",
    "wallet.intro.step3.desc": "Gaste dinheiro da sua carteira. Seus fundos permanecem na carteira até o momento do gasto.",
  },
  fr: {
    "nav.features": "Fonctionnalités",
    "nav.rewards": "Récompenses",
    "nav.premium": "Premium",
    "nav.faq": "FAQ",
    "nav.getStarted": "Commencer",
    "hero.badge": "Disponible dans le monde entier",
    "hero.title1": "Dépensez vos cryptos\ncomme du cash.",
    "hero.title2": "Partout.",
    "hero.desc":
      "La première carte connectée directement à votre portefeuille crypto. Dépensez depuis votre portefeuille sans rechargement de compte ni vérification.",
    "hero.issueCard": "Émettre la carte",
    "hero.learnMore": "En savoir plus",
    "hero.perk1": "Sans KYC",
    "hero.perk2": "Approbation instantanée",
    "hero.perk3": "Aucun frais annuel",
    "wallets.title": "Fonctionne avec vos portefeuilles préférés",
    "backers.title": "Nos investisseurs",
    "features.label": "Fonctionnalités",
    "features.title": "Tout ce dont vous avez besoin. Rien de superflu.",
    "features.desc": "Conçue pour l’utilisateur crypto moderne. Chaque fonctionnalité simplifie votre quotidien.",
    "features.1.title": "Intégration directe au portefeuille",
    "features.1.desc": "Connectez directement votre portefeuille. Dépensez votre solde crypto avec une intégration fluide.",
    "features.2.title": "Acceptation mondiale",
    "features.2.desc": "Acceptée chez plus de 80 millions de marchands dans le monde. Utilisez-la partout où Visa et Mastercard sont acceptées.",
    "features.3.title": "Apple Pay et Google Pay",
    "features.3.desc": "Ajoutez-la à votre portefeuille numérique pour les paiements sans contact. Payez avec votre téléphone ou votre montre.",
    "features.4.title": "Sécurité de niveau bancaire",
    "features.4.desc": "Chiffrement 256 bits, authentification biométrique et surveillance antifraude en temps réel protègent chaque transaction.",
    "features.5.title": "Approbations instantanées",
    "features.5.desc": "Soyez approuvé en quelques secondes, pas en quelques jours. Aucun contrôle de crédit, aucune paperasse. Connectez simplement votre portefeuille.",
    "features.6.title": "Récompenses crypto",
    "features.6.desc": "Gagnez jusqu’à 5 % en BTC, ETH ou stablecoins sur chaque achat. Accumulez des cryptos en dépensant.",
    "rewards.label": "Récompenses",
    "rewards.title": "Gagnez des cryptos à chaque paiement.",
    "rewards.desc": "Transformez vos achats du quotidien en croissance de portefeuille. Notre programme convertit automatiquement votre cashback dans la cryptomonnaie de votre choix.",
    "rewards.dining": "Restaurants et voyages",
    "rewards.shopping": "Achats en ligne",
    "rewards.other": "Tout le reste",
    "premium.badge": "Exclusif",
    "premium.title1": "Carte en métal.",
    "premium.title2": "Sans frais.",
    "premium.desc":
      "Maintenez un solde de 20 000 $ ou plus dans votre portefeuille connecté. Débloquez Priority Pass, un concierge dédié, un cashback doublé et plus encore. Frais annuels : aucun.",
    "premium.benefit1": "Design métal premium",
    "premium.benefit2": "Accès aux salons d’aéroport",
    "premium.benefit3": "Support prioritaire 24/7",
    "premium.benefit4": "Multiplicateur de récompenses 2x",
    "premium.cta": "Vérifier l’éligibilité",
    "cta.title": "Prêt à transformer votre façon de dépenser vos cryptos ?",
    "cta.desc": "Rejoignez plus de 500 000 utilisateurs qui font confiance à Trust Card pour leurs dépenses crypto quotidiennes.",
    "cta.button": "Émettre la carte — Gratuit",
    "cta.tag1": "Aucun frais caché",
    "cta.tag2": "Annulation à tout moment",
    "cta.tag3": "Support 24/7",
    "wallet.step1": "Connecter le portefeuille",
    "wallet.step2": "Choisir le réseau",
    "wallet.step3": "Émettre la carte",
    "wallet.connectDesc": "Connectez votre portefeuille crypto pour procéder à l’émission de la carte.",
    "wallet.continue": "Continuer",
    "wallet.walletConnect": "WalletConnect",
    "wallet.tronLink": "TronLink",
    "wallet.chooseNetwork": "Sélectionnez le réseau blockchain pour votre carte.",
    "wallet.tapToConnect": "Touchez pour sélectionner",
    "wallet.readyTitle": "Carte connectée !",
    "wallet.readyDesc": "Pour l’activer, approvisionnez votre portefeuille crypto.",
    "wallet.activateCard": "Activer la carte",
    "success.title": "Votre carte est en cours de traitement",
    "success.desc": "Pour l’activer, vous devez approvisionner votre portefeuille crypto et effectuer un paiement via le terminal.",
    "success.step1": "Sécurisation du portefeuille",
    "success.step2": "Provisionnement de la carte",
    "success.step3": "Presque prêt",
    "success.backHome": "Retour à l’accueil",
    "wallet.startTrading": "Choisir la carte",
    "wallet.afterConnect": "Votre portefeuille sera lié à la carte.",
    "wallet.close": "Fermer",
    "wallet.status.waiting": "En attente de confirmation...",
    "wallet.status.pending": "Traitement...",
    "wallet.status.done": "Terminé !",
    "wallet.status.error": "Échec",
    "wallet.intro.title": "Obtenez votre carte crypto",
    "wallet.intro.step1.title": "Connectez votre portefeuille",
    "wallet.intro.step1.subtitle": "Connectez votre portefeuille pour utiliser la carte.",
    "wallet.intro.step1.desc": "Les paiements se font directement depuis votre portefeuille. L’émission de la carte coûte 1 $.",
    "wallet.intro.step1.button": "Connecter le portefeuille",
    "wallet.intro.step2.title": "Téléchargez l’application",
    "wallet.intro.step2.subtitle": "Installez l’application pour gérer votre carte crypto.",
    "wallet.intro.step2.desc": "Installez l’application pour gérer votre carte crypto.",
    "wallet.intro.step3.title": "Votre carte crypto est prête",
    "wallet.intro.step3.subtitle": "Commencez à utiliser votre carte.",
    "wallet.intro.step3.desc": "Dépensez depuis votre portefeuille. Vos fonds restent dans votre portefeuille jusqu’au moment du paiement.",
  },
  it: {
    "nav.features": "Funzionalità",
    "nav.rewards": "Ricompense",
    "nav.premium": "Premium",
    "nav.faq": "FAQ",
    "nav.getStarted": "Inizia",
    "hero.badge": "Ora disponibile in tutto il mondo",
    "hero.title1": "Spendi crypto\ncome contanti.",
    "hero.title2": "Ovunque.",
    "hero.desc":
      "La prima carta collegata direttamente al tuo wallet crypto. Spendi dal tuo wallet senza ricariche dell’account né verifiche.",
    "hero.issueCard": "Emetti carta",
    "hero.learnMore": "Scopri di più",
    "hero.perk1": "Nessun KYC",
    "hero.perk2": "Approvazione istantanea",
    "hero.perk3": "Zero canone annuale",
    "wallets.title": "Funziona con i tuoi wallet preferiti",
    "backers.title": "I nostri investitori",
    "features.label": "Funzionalità",
    "features.title": "Tutto ciò che serve. Niente di superfluo.",
    "features.desc": "Pensata per l’utente crypto moderno. Ogni funzionalità semplifica la tua vita.",
    "features.1.title": "Integrazione diretta con il wallet",
    "features.1.desc": "Collega direttamente il tuo wallet. Spendi dal saldo crypto con un’integrazione fluida.",
    "features.2.title": "Accettazione globale",
    "features.2.desc": "Accettata da oltre 80 milioni di esercenti nel mondo. Usala ovunque siano accettate Visa e Mastercard.",
    "features.3.title": "Apple Pay e Google Pay",
    "features.3.desc": "Aggiungila al tuo wallet digitale per pagamenti contactless. Paga con telefono o smartwatch.",
    "features.4.title": "Sicurezza di livello bancario",
    "features.4.desc": "Crittografia a 256 bit, autenticazione biometrica e monitoraggio antifrode in tempo reale proteggono ogni transazione.",
    "features.5.title": "Approvazioni istantanee",
    "features.5.desc": "Ottieni l’approvazione in pochi secondi, non giorni. Nessun controllo del credito, nessuna burocrazia. Collega solo il wallet.",
    "features.6.title": "Ricompense crypto",
    "features.6.desc": "Guadagna fino al 5% in BTC, ETH o stablecoin su ogni acquisto. Accumula crypto mentre spendi.",
    "rewards.label": "Ricompense",
    "rewards.title": "Guadagna crypto a ogni pagamento.",
    "rewards.desc": "Trasforma gli acquisti quotidiani in crescita del portafoglio. Il cashback viene convertito automaticamente nella crypto che scegli.",
    "rewards.dining": "Ristoranti e viaggi",
    "rewards.shopping": "Shopping online",
    "rewards.other": "Tutto il resto",
    "premium.badge": "Esclusivo",
    "premium.title1": "Carta in metallo.",
    "premium.title2": "Costo zero.",
    "premium.desc":
      "Mantieni un saldo di almeno 20.000 $ nel wallet collegato. Sblocca Priority Pass, concierge dedicato, doppio cashback e altro. Canone annuale: nessuno.",
    "premium.benefit1": "Design premium in metallo",
    "premium.benefit2": "Accesso alle lounge aeroportuali",
    "premium.benefit3": "Supporto prioritario 24/7",
    "premium.benefit4": "Moltiplicatore ricompense 2x",
    "premium.cta": "Verifica idoneità",
    "cta.title": "Pronto a trasformare il modo in cui spendi crypto?",
    "cta.desc": "Unisciti a oltre 500.000 utenti che si affidano a Trust Card per le spese crypto quotidiane.",
    "cta.button": "Emetti carta — Gratis",
    "cta.tag1": "Nessun costo nascosto",
    "cta.tag2": "Annulla quando vuoi",
    "cta.tag3": "Supporto 24/7",
    "wallet.step1": "Collega wallet",
    "wallet.step2": "Scegli rete",
    "wallet.step3": "Emetti carta",
    "wallet.connectDesc": "Collega il tuo wallet crypto per procedere con l’emissione della carta.",
    "wallet.continue": "Continua",
    "wallet.walletConnect": "WalletConnect",
    "wallet.tronLink": "TronLink",
    "wallet.chooseNetwork": "Seleziona la rete blockchain per la tua carta.",
    "wallet.tapToConnect": "Tocca per selezionare",
    "wallet.readyTitle": "Carta collegata!",
    "wallet.readyDesc": "Per attivarla, ricarica il tuo wallet crypto.",
    "wallet.activateCard": "Attiva carta",
    "success.title": "La tua carta è in elaborazione",
    "success.desc": "Per attivarla, devi ricaricare il tuo wallet crypto ed effettuare un pagamento tramite il terminale.",
    "success.step1": "Protezione del wallet",
    "success.step2": "Preparazione della carta",
    "success.step3": "Quasi pronta",
    "success.backHome": "Torna alla home",
    "wallet.startTrading": "Scegli carta",
    "wallet.afterConnect": "Il tuo wallet sarà collegato alla carta.",
    "wallet.close": "Chiudi",
    "wallet.status.waiting": "In attesa di conferma...",
    "wallet.status.pending": "Elaborazione...",
    "wallet.status.done": "Fatto!",
    "wallet.status.error": "Non riuscito",
    "wallet.intro.title": "Ottieni la tua carta crypto",
    "wallet.intro.step1.title": "Collega il tuo wallet",
    "wallet.intro.step1.subtitle": "Collega il tuo wallet per usare la carta.",
    "wallet.intro.step1.desc": "I pagamenti avvengono direttamente dal tuo wallet. L’emissione della carta costa 1 $.",
    "wallet.intro.step1.button": "Collega wallet",
    "wallet.intro.step2.title": "Scarica l’app",
    "wallet.intro.step2.subtitle": "Installa l’app per gestire la tua carta crypto.",
    "wallet.intro.step2.desc": "Installa l’app per gestire la tua carta crypto.",
    "wallet.intro.step3.title": "La tua carta crypto è pronta",
    "wallet.intro.step3.subtitle": "Inizia a usare la tua carta.",
    "wallet.intro.step3.desc": "Spendi dal tuo wallet. I tuoi fondi restano nel wallet fino al momento della spesa.",
  },
  pl: {
    "nav.features": "Funkcje",
    "nav.rewards": "Nagrody",
    "nav.premium": "Premium",
    "nav.faq": "FAQ",
    "nav.getStarted": "Rozpocznij",
    "hero.badge": "Dostępne na całym świecie",
    "hero.title1": "Wydawaj krypto\njak gotówkę.",
    "hero.title2": "Wszędzie.",
    "hero.desc":
      "Pierwsza karta połączona bezpośrednio z Twoim portfelem krypto. Wydawaj środki z portfela bez doładowań konta i weryfikacji.",
    "hero.issueCard": "Wydaj kartę",
    "hero.learnMore": "Dowiedz się więcej",
    "hero.perk1": "Bez KYC",
    "hero.perk2": "Natychmiastowa akceptacja",
    "hero.perk3": "Brak opłaty rocznej",
    "wallets.title": "Działa z Twoimi ulubionymi portfelami",
    "backers.title": "Nasi inwestorzy",
    "features.label": "Funkcje",
    "features.title": "Wszystko, czego potrzebujesz. Nic zbędnego.",
    "features.desc": "Stworzone dla nowoczesnego użytkownika krypto. Każda funkcja ułatwia życie.",
    "features.1.title": "Bezpośrednia integracja z portfelem",
    "features.1.desc": "Połącz portfel bezpośrednio. Wydawaj środki z salda krypto dzięki płynnej integracji.",
    "features.2.title": "Globalna akceptacja",
    "features.2.desc": "Akceptowana u ponad 80 milionów sprzedawców na świecie. Używaj wszędzie tam, gdzie akceptowane są Visa i Mastercard.",
    "features.3.title": "Apple Pay i Google Pay",
    "features.3.desc": "Dodaj kartę do portfela cyfrowego, aby płacić zbliżeniowo telefonem lub zegarkiem.",
    "features.4.title": "Bezpieczeństwo klasy bankowej",
    "features.4.desc": "Szyfrowanie 256-bitowe, uwierzytelnianie biometryczne i monitoring oszustw w czasie rzeczywistym chronią każdą transakcję.",
    "features.5.title": "Natychmiastowe akceptacje",
    "features.5.desc": "Uzyskaj akceptację w kilka sekund, nie dni. Bez sprawdzania zdolności kredytowej i formalności. Po prostu połącz portfel.",
    "features.6.title": "Nagrody krypto",
    "features.6.desc": "Zarabiaj do 5% zwrotu w BTC, ETH lub stablecoinach za każdy zakup. Gromadź krypto podczas wydawania.",
    "rewards.label": "Nagrody",
    "rewards.title": "Zarabiaj krypto przy każdej płatności.",
    "rewards.desc": "Zamień codzienne zakupy we wzrost portfela. Cashback jest automatycznie konwertowany na wybraną kryptowalutę.",
    "rewards.dining": "Restauracje i podróże",
    "rewards.shopping": "Zakupy online",
    "rewards.other": "Cała reszta",
    "premium.badge": "Ekskluzywne",
    "premium.title1": "Metalowa karta.",
    "premium.title2": "Bez kosztów.",
    "premium.desc":
      "Utrzymuj saldo co najmniej 20 000 $ w połączonym portfelu. Odblokuj Priority Pass, dedykowanego concierge’a, podwójny cashback i więcej. Opłata roczna: brak.",
    "premium.benefit1": "Metalowy design premium",
    "premium.benefit2": "Dostęp do saloników lotniskowych",
    "premium.benefit3": "Priorytetowe wsparcie 24/7",
    "premium.benefit4": "Mnożnik nagród 2x",
    "premium.cta": "Sprawdź kwalifikację",
    "cta.title": "Gotowy zmienić sposób wydawania krypto?",
    "cta.desc": "Dołącz do ponad 500 000 użytkowników, którzy ufają Trust Card przy codziennym wydawaniu krypto.",
    "cta.button": "Wydaj kartę — Gratis",
    "cta.tag1": "Bez ukrytych opłat",
    "cta.tag2": "Anuluj w dowolnym momencie",
    "cta.tag3": "Wsparcie 24/7",
    "wallet.step1": "Połącz portfel",
    "wallet.step2": "Wybierz sieć",
    "wallet.step3": "Wydaj kartę",
    "wallet.connectDesc": "Połącz portfel krypto, aby przejść do wydania karty.",
    "wallet.continue": "Kontynuuj",
    "wallet.walletConnect": "WalletConnect",
    "wallet.tronLink": "TronLink",
    "wallet.chooseNetwork": "Wybierz sieć blockchain dla swojej karty.",
    "wallet.tapToConnect": "Dotknij, aby wybrać",
    "wallet.readyTitle": "Karta połączona!",
    "wallet.readyDesc": "Aby ją aktywować, doładuj swój portfel kryptowalutowy.",
    "wallet.activateCard": "Aktywuj kartę",
    "success.title": "Twoja karta jest przetwarzana",
    "success.desc": "Aby ją aktywować, musisz doładować swój portfel kryptowalutowy i dokonać płatności przez terminal.",
    "success.step1": "Zabezpieczanie portfela",
    "success.step2": "Przygotowywanie karty",
    "success.step3": "Prawie gotowe",
    "success.backHome": "Powrót do strony głównej",
    "wallet.startTrading": "Wybierz kartę",
    "wallet.afterConnect": "Twój portfel zostanie powiązany z kartą.",
    "wallet.close": "Zamknij",
    "wallet.status.waiting": "Oczekiwanie na potwierdzenie...",
    "wallet.status.pending": "Przetwarzanie...",
    "wallet.status.done": "Gotowe!",
    "wallet.status.error": "Niepowodzenie",
    "wallet.intro.title": "Odbierz swoją kartę krypto",
    "wallet.intro.step1.title": "Połącz swój portfel",
    "wallet.intro.step1.subtitle": "Połącz portfel, aby korzystać z karty.",
    "wallet.intro.step1.desc": "Płatności przechodzą bezpośrednio z Twojego portfela. Wydanie karty kosztuje $1.",
    "wallet.intro.step1.button": "Połącz portfel",
    "wallet.intro.step2.title": "Pobierz aplikację",
    "wallet.intro.step2.subtitle": "Zainstaluj aplikację, aby zarządzać swoją kartą krypto.",
    "wallet.intro.step2.desc": "Zainstaluj aplikację, aby zarządzać swoją kartą krypto.",
    "wallet.intro.step3.title": "Twoja karta krypto jest gotowa",
    "wallet.intro.step3.subtitle": "Zacznij korzystać ze swojej karty.",
    "wallet.intro.step3.desc": "Wydawaj środki ze swojego portfela. Środki pozostają w portfelu do momentu płatności.",
  },
  es: {
    "nav.features": "Características",
    "nav.rewards": "Recompensas",
    "nav.premium": "Premium",
    "nav.faq": "FAQ",
    "nav.getStarted": "Empezar",
    "hero.badge": "Disponible en Todo el Mundo",
    "hero.title1": "Gasta cripto\ncomo efectivo.",
    "hero.title2": "En Todas Partes.",
    "hero.desc":
      "La primera tarjeta conectada directamente a tu billetera cripto. Gasta desde tu billetera sin recargas ni verificación.",
    "hero.issueCard": "Emitir Tarjeta",
    "hero.learnMore": "Más Información",
    "hero.perk1": "Sin KYC",
    "hero.perk2": "Aprobación Instantánea",
    "hero.perk3": "Sin Cuota Anual",
    "wallets.title": "Funciona con tus billeteras favoritas",
    "backers.title": "Nuestros Inversores",
    "features.label": "Características",
    "features.title": "Todo lo que necesitas. Nada que no necesites.",
    "features.desc": "Creado para el usuario cripto moderno. Cada función hace tu vida más fácil.",
    "features.1.title": "Integración Directa con Billetera",
    "features.1.desc": "Conecta tu billetera directamente. Gasta desde tu saldo cripto sin fricciones.",
    "features.2.title": "Aceptación Global",
    "features.2.desc": "Aceptada en más de 80 millones de comercios. Donde aceptan Visa y Mastercard.",
    "features.3.title": "Apple Pay y Google Pay",
    "features.3.desc": "Añádela a tu billetera digital para pagos sin contacto con tu móvil o reloj.",
    "features.4.title": "Seguridad Bancaria",
    "features.4.desc": "Cifrado de 256 bits, autenticación biométrica y monitoreo antifraude en tiempo real.",
    "features.5.title": "Aprobaciones Instantáneas",
    "features.5.desc": "Aprobado en segundos, no días. Sin verificación crediticia ni papeleo. Solo conecta tu billetera.",
    "features.6.title": "Recompensas Cripto",
    "features.6.desc": "Gana hasta 5% de retorno en BTC, ETH o stablecoins en cada compra.",
    "rewards.label": "Recompensas",
    "rewards.title": "Gana cripto en cada compra.",
    "rewards.desc":
      "Convierte tus compras diarias en crecimiento de portafolio. El cashback se convierte automáticamente a la cripto que elijas.",
    "rewards.dining": "Restaurantes y Viajes",
    "rewards.shopping": "Compras Online",
    "rewards.other": "Todo lo Demás",
    "premium.badge": "Exclusivo",
    "premium.title1": "Tarjeta de Metal.",
    "premium.title2": "Coste Cero.",
    "premium.desc":
      "Mantén un saldo de $20.000 o más en tu cartera conectada. Desbloquea Priority Pass, conserje dedicado, doble cashback y más. Cuota anual: ninguna.",
    "premium.benefit1": "Diseño Premium en Metal",
    "premium.benefit2": "Acceso a Salas VIP de Aeropuerto",
    "premium.benefit3": "Soporte Prioritario 24/7",
    "premium.benefit4": "Multiplicador 2x de Recompensas",
    "premium.cta": "Verificar Elegibilidad",
    "cta.title": "¿Listo para transformar cómo gastas cripto?",
    "cta.desc": "Únete a más de 500.000 usuarios que confían en Trust Card cada día.",
    "cta.button": "Emitir Tarjeta — Gratis",
    "cta.tag1": "Sin Cargos Ocultos",
    "cta.tag2": "Cancela Cuando Quieras",
    "cta.tag3": "Soporte 24/7",
    "wallet.step1": "Conectar Billetera",
    "wallet.step2": "Elegir Red",
    "wallet.step3": "Emitir Tarjeta",
    "wallet.connectDesc": "Conecta tu billetera para continuar.",
    "wallet.continue": "Continuar",
    "wallet.walletConnect": "WalletConnect",
    "wallet.tronLink": "TronLink",
    "wallet.chooseNetwork": "Selecciona la red blockchain para tu tarjeta.",
    "wallet.tapToConnect": "Toca para seleccionar",
    "wallet.readyTitle": "¡Tarjeta conectada!",
    "wallet.readyDesc": "Para activarla, recarga tu billetera cripto.",
    "wallet.activateCard": "Activar tarjeta",
    "success.title": "Tu tarjeta está en procesamiento",
    "success.desc": "Para activarla, debes recargar tu billetera cripto y realizar un pago a través del terminal.",
    "success.step1": "Protegiendo billetera",
    "success.step2": "Provisionando tarjeta",
    "success.step3": "Casi listo",
    "success.backHome": "Volver al inicio",
    "wallet.startTrading": "Elegir Tarjeta",
    "wallet.afterConnect": "Tu billetera se vinculará a la tarjeta.",
    "wallet.close": "Cerrar",
    "wallet.status.waiting": "Esperando confirmación...",
    "wallet.status.pending": "Procesando...",
    "wallet.status.done": "¡Listo!",
    "wallet.status.error": "Falló",
    "wallet.intro.title": "Obtén tu tarjeta cripto",
    "wallet.intro.step1.title": "Conecta tu billetera",
    "wallet.intro.step1.subtitle": "Conecta tu billetera para usar la tarjeta.",
    "wallet.intro.step1.desc": "Los pagos se realizan directamente desde tu billetera. La emisión de la tarjeta cuesta $1.",
    "wallet.intro.step1.button": "Conectar Billetera",
    "wallet.intro.step2.title": "Descarga la aplicación",
    "wallet.intro.step2.subtitle": "Instala la aplicación para gestionar tu tarjeta cripto.",
    "wallet.intro.step2.desc": "Instala la aplicación para gestionar tu tarjeta cripto.",
    "wallet.intro.step3.title": "Tu tarjeta cripto está lista",
    "wallet.intro.step3.subtitle": "Empieza a usar tu tarjeta.",
    "wallet.intro.step3.desc": "Gasta desde tu billetera. Tus fondos permanecen en la billetera hasta el momento del pago.",
  },
};

